/**
 * OpenNebula (Techev Cloud) — shared data-layer infra for the Compute / Networking
 * / Kubernetes / Database modules. Ported from the legacy @console
 * `Internal.Opennebula` connectors.
 *
 * Compute (s10003) traffic now enters the @mgmt v3 strangler gateway, which
 * authorizes the request then forwards each s10003 family to the legacy Ruby app
 * (single front door, one authz model). So `serviceApi` exported here is the v3
 * client and `onPath()` builds v3 paths:
 *   direct:  `${computeApi}/<path>` + X-Compute-Metadata-ID (APP_COMPUTE_API_URL set)
 *   @mgmt:   `${computeApi}/services/:serviceId/s10003/<path>`  (fallback)
 * Within this module: compute calls use `serviceApi` (from here, → v3). NON-compute
 * endpoints the proxy does NOT cover (`/v2/services/:id`, `/v2/zones/*`,
 * `/workers/*`) must import the raw v2 client from `@/api/client` instead.
 *
 * NOTE: the `s10003` provider segment IS required for compute and is preserved on
 * v3 (the proxy mounts `/services/:id/s10003/*`). A correctly-seeded service
 * returns the real VMs; the segment-less path returns the wrong list (a single
 * RKE2/k8s node, not the user's compute VMs).
 */
import { getHttpClient } from '@vnetwork/margarita-core/http';

const computeApi = getHttpClient('computeApi');

// ---- Service scope + path ------------------------------------------------

/** Provider scope that resolves to this module (see lib/service-categories.ts). */
export const OPENNEBULA_SCOPE = 'Internal.Opennebula';

/** OpenNebula IaaS provider code (preserved on the v3 gateway path). */
const S10003 = 's10003';

/** Build a service-scoped path: onPath(id, 'instances') → /services/id/s10003/instances */
/**
 * How long a hotplug is waited for: attaching or detaching a NIC, a disk, a
 * security group or an address on a running machine.
 *
 * Compute answers once OpenNebula has finished and it has checked the result,
 * retrying while the machine is still busy — up to about 90s, as Rails did. The
 * client's 30s default gave up first: the change went through and the screen
 * said it failed (a NIC detach took 22s in testing).
 */
export const HOTPLUG_TIMEOUT_MS = 120_000;

export function onPath(serviceId: string, path: string): string {
  return `/services/${serviceId}/${S10003}/${path.replace(/^\/+/, '')}`;
}

/**
 * The compute client, `computeApi`. Use with `onPath()`.
 *
 * This IS `computeApi`, the one compute client: every compute caller reaches the direct
 * service through it. With `APP_COMPUTE_API_URL` unset that client's base is
 * `serviceApiV3Url`, so `onPath()` paths go to the @mgmt proxy as they are; set, the compute
 * scope interceptor rewrites them and adds `X-Compute-Metadata-ID`.
 */
export const serviceApi = computeApi;

// ---- Response envelope ---------------------------------------------------
// s10003 wraps payloads as `{ status, data, message?, total? }`. `unwrap` returns the inner
// `data`; it lives in `@vnetwork/margarita-shell/envelope` (Database uses it too) and is
// re-exported here so every caller keeps this import.
export { unwrap } from '@vnetwork/margarita-shell/envelope';

/** Stable query-string fragment for keys/requests (sorted, no `qs` dep). */
export function toQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const k of Object.keys(params).sort()) {
    const v = params[k];
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => sp.append(`${k}[]`, String(x)));
    else sp.append(k, String(v));
  }
  return sp.toString();
}

// ---- Query keys ----------------------------------------------------------
// Array-based keys (TanStack v5 convention), scoped by serviceId so switching
// services never crosses caches. Add per-feature keys here as modules land.

export const onKeys = {
  all: ['on'] as const,
  service: (s: string) => ['on', s] as const,

  // Compute / instances
  instances: (s: string) => ['on', s, 'instances'] as const,
  instance: (s: string, id: string) => ['on', s, 'instances', id] as const,
  instanceSummary: (s: string, id: string) => ['on', s, 'instances', id, 'summary'] as const,
  instanceMonitoring: (s: string, id: string, q?: Record<string, unknown>) =>
    ['on', s, 'instances', id, 'monitoring', toQuery(q)] as const,
  instanceConsole: (s: string, id: string) => ['on', s, 'instances', id, 'vnc'] as const,
  instanceHealth: (s: string, id: string) => ['on', s, 'instances', id, 'healthz'] as const,
  instanceSflow: (s: string, id: string, q?: Record<string, unknown>) =>
    ['on', s, 'instances', id, 'sflow', toQuery(q)] as const,

  // Shared resource lists (used across Compute + Networking modules)
  networks: (s: string) => ['on', s, 'networks'] as const,
  network: (s: string, id: string) => ['on', s, 'networks', id] as const,
  networkIncremental: (s: string) => ['on', s, 'private_network_incremental'] as const,
  reservedNetworks: (s: string) => ['on', s, 'networks', 'reserves'] as const,
  routers: (s: string) => ['on', s, 'routers'] as const,
  router: (s: string, id: string) => ['on', s, 'routers', id] as const,
  routerRoutes: (s: string, id: string) => ['on', s, 'routers', id, 'routes'] as const,
  routerNatRules: (s: string, id: string) => ['on', s, 'routers', id, 'nat_rules'] as const,
  routerStatus: (s: string, id: string) => ['on', s, 'routers', id, 'status'] as const,
  vpcs: (s: string) => ['on', s, 'vpcs'] as const,
  vpc: (s: string, id: string) => ['on', s, 'vpcs', id] as const,
  peeringSubnets: (s: string) => ['on', s, 'peering', 'subnets'] as const,
  peeringGroups: (s: string) => ['on', s, 'peering', 'groups'] as const,
  peeringGroup: (s: string, id: string) => ['on', s, 'peering', 'groups', id] as const,
  elasticIps: (s: string) => ['on', s, 'elastic_ips'] as const,
  vpnGateways: (s: string) => ['on', s, 'vpn', 'gateways'] as const,
  vpnIke: (s: string) => ['on', s, 'vpn', 'ike'] as const,
  vpnIpsec: (s: string) => ['on', s, 'vpn', 'ipsec'] as const,
  vpnS2s: (s: string) => ['on', s, 'vpn', 's2s'] as const,
  lbs: (s: string) => ['on', s, 'lbs'] as const,
  lb: (s: string, id: string) => ['on', s, 'lbs', id] as const,
  lbStats: (s: string, id: string) => ['on', s, 'lbs', id, 'stats'] as const,
  lbForwards: (s: string, id: string) => ['on', s, 'lbs', id, 'forwards'] as const,
  lbMonitoring: (s: string, id: string, q?: Record<string, unknown>) =>
    ['on', s, 'lbs', id, 'monitoring', toQuery(q)] as const,
  certificates: (s: string) => ['on', s, 'certificates'] as const,
  secgroups: (s: string) => ['on', s, 'secgroups'] as const,
  secgroup: (s: string, id: string) => ['on', s, 'secgroups', id] as const,
  sshKeys: (s: string) => ['on', s, 'sshkeys'] as const,
  volumes: (s: string) => ['on', s, 'volumes'] as const,
  snapshots: (s: string) => ['on', s, 'snapshots'] as const,
  instanceSnapshots: (s: string, id: string) => ['on', s, 'instances', id, 'snapshots'] as const,
  scheduledActions: (s: string, id: string) => ['on', s, 'instances', id, 'schedules'] as const,
  backups: (s: string) => ['on', s, 'backups'] as const,
  backup: (s: string, bid: string) => ['on', s, 'backups', bid] as const,
  images: (s: string) => ['on', s, 'images'] as const,
  marketplaces: (s: string) => ['on', s, 'marketplaces'] as const,
  quotas: (s: string) => ['on', s, 'quotas'] as const,
  usages: (s: string, q?: Record<string, unknown>) => ['on', s, 'usages', toQuery(q)] as const,
} as const;

// ---- Instance lifecycle types --------------------------------------------

/** Actions POSTed to `/instances/:id/perform`. */
export type InstanceAction =
  | 'poweron'
  | 'poweroff'
  | 'reboot'
  | 'suspend'
  | 'resume'
  | 'terminate'
  | 'resize';

/** External "an action is in flight" flag that drives adaptive polling. */
export type WaitingState = boolean | InstanceAction | string;

// ---- Adaptive polling ----------------------------------------------------
// While an action is in flight we poll faster, then back off. Each action has a
// profile tuned to how long that transition typically takes. Ported verbatim
// from @console hooks/use-adaptive-polling (intervals unchanged).

interface ActionPollingConfig {
  initialInterval: number;
  maxInterval: number;
  timeout: number;
  backoffMultiplier: number;
}

const ACTION_POLLING_CONFIG: Record<InstanceAction | 'default', ActionPollingConfig> = {
  poweron: { initialInterval: 2000, maxInterval: 8000, timeout: 120_000, backoffMultiplier: 1.3 },
  poweroff: { initialInterval: 3000, maxInterval: 10_000, timeout: 180_000, backoffMultiplier: 1.4 },
  reboot: { initialInterval: 2000, maxInterval: 5000, timeout: 60_000, backoffMultiplier: 1.2 },
  resume: { initialInterval: 2000, maxInterval: 8000, timeout: 120_000, backoffMultiplier: 1.3 },
  suspend: { initialInterval: 2000, maxInterval: 8000, timeout: 120_000, backoffMultiplier: 1.3 },
  terminate: { initialInterval: 2000, maxInterval: 10_000, timeout: 300_000, backoffMultiplier: 1.5 },
  resize: { initialInterval: 3000, maxInterval: 10_000, timeout: 180_000, backoffMultiplier: 1.3 },
  default: { initialInterval: 3000, maxInterval: 10_000, timeout: 180_000, backoffMultiplier: 1.3 },
};

/**
 * How long an action is allowed to hold the waiting flag before a caller gives
 * up on it. The profiles have always carried a timeout — nothing read it, so a
 * flag that was never cleared kept the page polling for as long as it was open.
 */
export function getPollingTimeout(waiting: WaitingState): number {
  if (typeof waiting === 'string') {
    return (ACTION_POLLING_CONFIG[waiting as InstanceAction] ?? ACTION_POLLING_CONFIG.default).timeout;
  }
  return ACTION_POLLING_CONFIG.default.timeout;
}

/**
 * Polling interval for a react-query `refetchInterval`, derived from the current
 * waiting state. `false` = no polling (idle).
 */
export function getPollingInterval(waiting: WaitingState): number | false {
  if (!waiting) return false;
  if (typeof waiting === 'string') {
    return (ACTION_POLLING_CONFIG[waiting as InstanceAction] ?? ACTION_POLLING_CONFIG.default)
      .initialInterval;
  }
  return ACTION_POLLING_CONFIG.default.initialInterval;
}
